import React, { useContext, TouchEvent, MouseEvent } from 'react';
import HeaderNavBarContext from './context/HeaderNavBarContext';

import fireEvent from './utils/fireEvent';
import getFormattedMenuLinks from './utils/getFormattedMenuLinks';
import useTarget from './hooks/useTarget';

import Box from '../../components/Box/Box';
import Icon from '../../components/Icon/Icon';
import { TMenuLink } from '../../types/TMenuLink';
import LoginButton from './utils/LoginButton';
import { TTranslations } from '../../types/THeaderConfig';

type THamburgerMenu = {
  isVisible: boolean;
  hideHamburgerMenu: () => void;
  menuLinks: TMenuLink[];
  className?: string;
  translations?: TTranslations;
};

type TEvent = TouchEvent<HTMLButtonElement | HTMLAnchorElement> | MouseEvent<HTMLButtonElement | HTMLAnchorElement>;

const HamburgerMenu = ({ isVisible, hideHamburgerMenu, menuLinks, translations }: THamburgerMenu) => {
  const { userDetails, userLinks, headerLinks, ssoLinks } = useContext(HeaderNavBarContext);
  const loginTarget = useTarget('');

  if (!userLinks) {
    return null;
  }

  const handlePlaceAd = (event: TEvent) => {
    const placeAdEvent = fireEvent(
      'placead',
      {},
      {
        cancelable: true,
        node: event.target as HTMLElement,
      },
    );

    if (placeAdEvent.defaultPrevented) {
      event.preventDefault();
    }
  };

  const onTouchStart = (e: TEvent) => {
    const menu = e.target as HTMLElement;
    // prevent scroll-through if menu scrolled to the same top or bottom
    if (menu.scrollTop === 0) {
      menu.scrollTop = 1;
    } else if (menu.scrollTop + menu.offsetHeight === menu.scrollHeight) {
      menu.scrollTop -= 1;
    }
  };

  const onTouchMove = (e: TEvent) => {
    const menu = e.target as HTMLElement;
    if (menu.scrollTop === 0) {
      e.preventDefault();
    }
  };

  // TODO: fix this!
  const { isLoggedIn } = userDetails!;

  return (
    <>
      {isVisible && (
        <Box
          as="div"
          role="presentation"
          onClick={hideHamburgerMenu}
          onTouchStart={(e) => e.preventDefault()}
          parent="HamburgerMenu"
          name="overlay"
          data-role="hamburgerOverlay"
        />
      )}

      <Box
        as="div"
        name="HamburgerMenu"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        data-role="hamburgerMenu"
      >
        <Box as="ul" parent="Nav" name="pills">
          <Box as="li" parent="Nav" name="pill" modifiers="header">
            {translations?.menuTitle}
            <Box
              as="a"
              parent="HamburgerMenu"
              name="close"
              onClick={hideHamburgerMenu}
              role="button"
              tabIndex={-1}
              data-role="hamburgerClose"
              aria-hidden="true"
            >
              <Icon name="close" size="m" />
            </Box>
          </Box>

          {userLinks.placeAd && (
            <>
              <li>
                <a
                  href={userLinks.placeAd.url}
                  onClick={handlePlaceAd}
                  data-role={userLinks.placeAd.role}
                  tabIndex={-1}
                >
                  {userLinks.placeAd.text}
                </a>
              </li>
              <Box as="li" parent="Nav" name="pill" modifiers="separator" />
            </>
          )}

          {Boolean(isLoggedIn && userLinks.shoppingCart) && (
            <li>
              <a href={userLinks.shoppingCart.url} data-role={userLinks.shoppingCart.role} tabIndex={-1}>
                {userLinks.shoppingCart.text}
              </a>
            </li>
          )}

          {userLinks.messages && (
            <li>
              <a href={userLinks.messages.url} data-role={userLinks.messages.role} tabIndex={-1}>
                {userLinks.messages.text}
                {Boolean(userLinks.messages.badge.count) && <span>({userLinks.messages.badge.count})</span>}
              </a>
            </li>
          )}

          {isLoggedIn && getFormattedMenuLinks(menuLinks).map(({ label, value }) => <li key={value}>{label}</li>)}

          <Box as="li" parent="Nav" name="pill" modifiers="separator" />

          {headerLinks?.map(({ text, url, target = '_blank' }) => (
            <li key={text}>
              <a href={url} target={target} tabIndex={-1}>
                {text}
              </a>
            </li>
          ))}

          <Box as="li" parent="Nav" name="pill" modifiers="separator" />

          {userLinks.privacy && (
            <li key={'Privacyvoorkeuren'}>
              <a href={userLinks.privacy.url} target={'_blank'} tabIndex={-1}>
                {userLinks.privacy.text}
              </a>
            </li>
          )}

          <LoginButton isLoggedIn={Boolean(isLoggedIn)} ssoLinks={ssoLinks} loginTarget={loginTarget} />

          {userLinks.localeAlt && (
            <>
              <Box as="li" parent="Nav" name="pill" modifiers="separator" />
              <li>
                <a href={userLinks.localeAlt.url} data-role={userLinks.localeAlt.role}>
                  {userLinks.localeAlt.textLong}
                </a>
              </li>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HamburgerMenu;
